import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  Tooltip,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  InputLabel,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Menu,
  ListItemIcon,
  Divider
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  FolderOpen as FolderIcon,
  Visibility as VisibilityIcon,
  ContentCopy as ContentCopyIcon,
  DriveFileMove as DriveFileMoveIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon
} from "@mui/icons-material";
import { ConfigProvider, Spin } from 'antd';
import './FormCard.css';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import FolderSelect from './FolderSelect'; // Yeni bileşeni import et

const FormCard = ({ form, selected, onSelect, onEdit, onDelete, onCopy, onShare, onMoveToFolder, folders, onFolderDialogOpen, onToggleStatus }) => {
  const { t } = useTranslation();
  
  // Debug için form verilerini logla
  console.log('FormCard form verisi:', form);
  // Sadece gerekli state'leri tut
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [folderOptions, setFolderOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [moveLoading, setMoveLoading] = useState(false);
  const [copyLoading, setCopyLoading] = useState(false);
  const selectRef = useRef(null);
  const selectContainerRef = useRef(null);
  const [targetFolderId, setTargetFolderId] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  
  // Redux'tan token bilgilerini al
  const appToken = useSelector((state) => state.appToken?.apptoken);
  const userData = useSelector((state) => state.loginData?.loginData);
  
  // Form bilgilerini güvenli bir şekilde çıkar
  const formId = form?.id || "defaultId";
  const formTitle = form?.title || t('untitled_form');
  const formDescription = form?.description || t('no_form_description');
  const formContent = form?.formContent || JSON.stringify({
    id: formId,
    title: formTitle,
    /* parentId: parentId.join(','), */
    description: formDescription,
/*     formContent: formData, */
    fields: form?.fields || [],
    settings: form?.settings || {},
    isPublished: true,
    publishedAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });
  const formFields = form?.fields || []; // Eğer formFields yoksa boş bir dizi kullanabilirsiniz
  const formSettings = form?.settings || {}; // Eğer formSettings yoksa boş bir obje kullanabilirsiniz
  
  // Renkleri belirle
  const colors = ['#f50', '#2db7f5', '#87d068', '#108ee9', '#722ed1', '#eb2f96', '#faad14', '#52c41a'];

  // Klasörleri getir
 /*  useEffect(() => {
    fetchFolders();
  }, []); */ // Bağımlılıklar dizisi boş olduğu için, bu useEffect yalnızca bileşen mount edildiğinde çalışır.
  
  const fetchFolders = async () => {
    setLoading(true);
    try {
      const response = await axios.get('https://app.hhoms.com/api/form/Categories/list/All', {
        headers: { 
          ApplicationToken: appToken, 
          User: userData.id 
        }
      });

      if (response.data && response.data.formCategories) {
        // İç içe klasör yapısını düzleştir
        const flattenedFolders = flattenFolderStructure(response.data);
        setFolderOptions(flattenedFolders);
      } else {
        toast.error('Klasör verileri alınamadı');
      }
    } catch (error) {
      console.error('Klasörleri yüklerken hata:', error);
      toast.error(`Klasörleri yüklerken hata: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Klasör yapısını düzleştiren yardımcı fonksiyon
  const flattenFolderStructure = (categories) => {
    if (!categories || !categories.formCategories) return [];
    
    const flatten = (folders, level = 0) => {
      let result = [];
      
      folders.forEach(folder => {
        result.push({
          value: folder.id.toString(),
          label: folder.title,
          fullPath: folder.title,
          level: level,
          parentId: folder.parentId
        });

        if (folder.detail && folder.detail.length > 0) {
          const children = flatten(folder.detail, level + 1);
          result = result.concat(children);
        }
      });

      return result;
    };

    return flatten(categories.formCategories);
  };

  const handleFolderSelect = (event) => {
    setSelectedFolders(event.target.value);
  };

  const handleFolderDialogOpen = async () => {
    setLoading(true);
    try {
      const response = await axios.get('https://app.hhoms.com/api/form/Categories/parent/list', {
        headers: { 
          ApplicationToken: appToken, 
          User: userData.id 
        }
      });

      if (response.data) {
        const flattenedFolders = flattenFolderStructure(response.data);
        setFolderOptions(flattenedFolders);
        
        // Form'un mevcut klasörlerini seç
        const parentIds = form.parentId ? form.parentId.split(',') : [];
        setSelectedFolders(parentIds);
      }
    } catch (error) {
      console.error('Klasörler yüklenirken hata:', error);
      toast.error('Klasörler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
      setFolderDialogOpen(true);
    }
  };

  const handleFolderDialogClose = () => {
    setFolderDialogOpen(false);
  };

  // Formu klasöre taşı
  const handleMoveForm = async () => {
    if (selectedFolders.length === 0) {
      toast.warning('Lütfen en az bir klasör seçin');
      return;
    }

    setMoveLoading(true);
    try {
      const payload = {
        formId:  form.formId,
        parentId: selectedFolders.join(',')
      };

      const response = await axios.put(
        `https://app.hhoms.com/api/form/Libraries/parent`,
        payload,
        {
          headers: { 
            ApplicationToken: appToken, 
            User: userData.id,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200 || response.status === 201) {
        // Modal'ı kapat
        setFolderDialogOpen(false);
        
        // Başarı mesajını göster
        toast.success('Form başarıyla klasöre taşındı', {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        // Parent component'e bildir
        if (onMoveToFolder) {
          onMoveToFolder(form.id || form.formId, selectedFolders[0]);
        }

        // Custom event ile yenileme tetikle
        const event = new CustomEvent('formMoved', {
          detail: {
            formId: form.id || form.formId,
            newFolderId: selectedFolders[0]
          }
        });
        window.dispatchEvent(event);

        // State'i temizle
        setSelectedFolders([]);
      } else {
        throw new Error('Form taşıma işlemi başarısız oldu');
      }
    } catch (error) {
      console.error('Form taşınırken hata:', error);
      
      toast.error('Form taşınırken bir hata oluştu', {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setMoveLoading(false);
    }
  };
  
  // Klasör seçimi değiştiğinde
  const handleFolderChange = (value) => {
    console.log("Klasör seçildi:", value);
    setTargetFolderId(value);
  };
  
  const handleUpdateFormParentIds = async () => {
    if (selectedFolders.length === 0) {
      toast.warning('Lütfen en az bir klasör seçin');
      return;
    }
  
    if (!formId || !userData.id) {
      toast.error('Form ID veya kullanıcı bilgisi eksik');
      return;
    }
  
    setMoveLoading(true);
    try {
      const updatePromises = selectedFolders.map(folderId =>
        axios.put(`https://app.hhoms.com/api/form/parent/${formId}/${folderId}`, {}, {
          headers: { 
            ApplicationToken: appToken, 
            User: userData.id 
          }
        })
      );
  
      await Promise.all(updatePromises);
      toast.success('Formun klasör bilgileri başarıyla güncellendi');
    } catch (error) {
      console.error('Formun klasör bilgileri güncellenirken hata oluştu:', error);
      toast.error(`Formun klasör bilgileri güncellenirken bir hata oluştu: ${error.response?.data?.message || error.message}`);
    } finally {
      setMoveLoading(false);
    }
  };
  
  const handleLogFormId = () => {
    console.log("Seçilen formun ID'si:", form.id);
  };
  
  const handleCopyForm = async () => {
    setCopyLoading(true);
    try {
      // Önce mevcut formun tüm detaylarını alalım
      const formDetailResponse = await axios.get(
        `https://app.hhoms.com/api/form/Libraries/${form.formId}`,
        {
          headers: {
            ApplicationToken: appToken,
            User: userData.id
          }
        }
      );

      if (!formDetailResponse.data?.formLibraries?.[0]) {
        throw new Error('Form detayları alınamadı');
      }

      const originalForm = formDetailResponse.data.formLibraries[0];
      
      // formContent string'ini parse edelim
      let parsedFormContent;
      try {
        parsedFormContent = typeof originalForm.formContent === 'string' 
          ? JSON.parse(originalForm.formContent)
          : originalForm.formContent;
      } catch (error) {
        console.error('Form içeriği parse edilemedi:', error);
        parsedFormContent = originalForm.formContent;
      }

      // 6 haneli rastgele bir formId oluştur
      const newFormId = Math.floor(100000 + Math.random() * 900000).toString();

      // Fields alanlarını kopyala
      const fields = parsedFormContent.fields || [];
      
      // Settings alanlarını kopyala
      const settings = parsedFormContent.settings || {};

      // Yeni form için veriyi hazırlayalım
      const formContent = {
        id: newFormId,
        title: `${originalForm.title} (Kopya)`,
        description: parsedFormContent.description || '',
        fields: fields,
        settings: settings,
        isPublished: true,
        publishedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        formLink: ''
      };

      const formData = {
        id: newFormId,
        title: `${originalForm.title} (Kopya)`,
        description: parsedFormContent.description || '',
        formId: newFormId,
        fields: fields,
        formContent: formContent,
        isActive: originalForm.isActive,
        parentId: originalForm.parentId || '0'
      };

      // Yeni formu oluşturalım
      const response = await axios.post(
        'https://app.hhoms.com/api/form/Libraries',
        formData,
        {
          headers: {
            ApplicationToken: appToken,
            User: userData.id,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data) {
        toast.success('Form başarıyla çoğaltıldı');
        
        // Yeni form oluşturulduğunda custom event fırlat
        const event = new CustomEvent('formCopied', {
          detail: {
            newForm: response.data
          }
        });
        window.dispatchEvent(event);
      }
    } catch (error) {
      console.error('Form çoğaltılırken hata:', error);
      toast.error(`Form çoğaltılırken bir hata oluştu: ${error.message}`);
    } finally {
      setCopyLoading(false);
    }
  };
  
  // Event listener'ı ekle
  useEffect(() => {
    const handleFormMoved = (event) => {
      const { formId, newFolderId } = event.detail;
      if (typeof onMoveToFolder === 'function') {
        onMoveToFolder(formId, newFolderId);
      }
    };

    window.addEventListener('formMoved', handleFormMoved);
    
    return () => {
      window.removeEventListener('formMoved', handleFormMoved);
    };
  }, [onMoveToFolder]);
  
  return (
    <>
      <Card
        sx={{
          width: '100%',
          height: '180px',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '12px',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)',
          border: selected ? '2px solid #3B82F6' : '1px solid rgba(0, 0, 0, 0.08)',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          overflow: 'hidden',
          position: 'relative',
          backgroundColor: 'white',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            borderColor: selected ? '#3B82F6' : 'rgba(59, 130, 246, 0.2)'
          }
        }}
        onClick={onSelect}
      >
        {/* Üst Kısım - Başlık ve Menü İkonu */}
        <Box sx={{ 
          p: 1.5, 
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flex: 1
        }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Tooltip title={formTitle} placement="top">
              <Typography 
                variant="h6" 
                sx={{
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: '#1e293b',
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  mb: 1.5
                }}
              >
                {formTitle}
              </Typography>
            </Tooltip>
            
                          {/* Yanıt Sayısı ve Durum Butonları */}
              <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                {/* Yanıt Sayısı Butonu */}
                <Box
                  sx={{
                    backgroundColor: '#3B82F6',
                    color: 'white',
                    px: 2,
                    py: 0.5,
                    borderRadius: '20px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 'fit-content'
                  }}
                >
                  {form?.records || 0} Yanıt
                </Box>
                
                {/* Durum Toggle Butonu */}
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onToggleStatus) {
                      onToggleStatus(form?.id, form?.isActive);
                    }
                  }}
                  sx={{
                    backgroundColor: form?.isActive ? '#10b981' : '#6b7280',
                    color: 'white',
                    px: 2,
                    py: 0.5,
                    borderRadius: '20px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 'fit-content',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: form?.isActive ? '#059669' : '#4b5563',
                      transform: 'scale(1.05)'
                    }
                  }}
                >
                  {form?.isActive ? 'Aktif' : 'Pasif'}
                </Box>
              </Box>
          </Box>
          
          {/* Menü İkonu */}
          <IconButton 
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchorEl(e.currentTarget);
            }} 
            size="small"
            sx={{
              p: 0.5,
              color: '#64748b',
              '&:hover': {
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                color: '#3B82F6'
              }
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Alt Kısım - Tarihler */}
        <Box sx={{ 
          p: 1.5, 
          borderTop: '1px solid rgba(0, 0, 0, 0.06)',
          backgroundColor: '#f8fafc',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5
        }}>
          {/* Oluşturulma Tarihi */}
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#64748b',
              fontSize: '0.75rem',
              fontWeight: 500
            }}
          >
            Oluşturulma: {form?.createDate ? 
              (() => {
                try {
                  return new Date(form.createDate).toLocaleDateString('tr-TR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  });
                } catch (error) {
                  console.error('Tarih parse hatası:', error, form.createDate);
                  return 'Geçersiz tarih';
                }
              })() : 
              'Belirtilmemiş'}
          </Typography>
          
          {/* Güncelleme Tarihi */}
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#64748b',
              fontSize: '0.75rem',
              fontWeight: 500
            }}
          >
            Güncelleme: {form?.editDate ? 
              (() => {
                try {
                  return new Date(form.editDate).toLocaleDateString('tr-TR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  });
                } catch (error) {
                  console.error('Tarih parse hatası:', error, form.editDate);
                  return 'Geçersiz tarih';
                }
              })() : 
              'Belirtilmemiş'}
          </Typography>
        </Box>
      </Card>

      {/* Menü */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
        PaperProps={{ 
          elevation: 8,
          sx: { 
            borderRadius: '8px',
            minWidth: '180px'
          } 
        }}
      >
        <MenuItem onClick={() => {
          const formUrl = `/form/${form?.formId || form?.id}`;
          window.open(formUrl, '_blank');
          setMenuAnchorEl(null);
        }}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Önizleme</Typography>
        </MenuItem>

        <MenuItem onClick={() => {
          const shareUrl = `https://panel.formclicker.com/user-login?formId=${form?.formId || form?.id}`;
          window.open(shareUrl, '_blank');
          setMenuAnchorEl(null);
        }}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Paylaş</Typography>
        </MenuItem>

        <MenuItem onClick={() => {
          const editUrl = `/form-edit/${form?.formId || form?.id}`;
          window.open(editUrl, '_blank');
          setMenuAnchorEl(null);
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Düzenle</Typography>
        </MenuItem>

        <MenuItem onClick={() => {
          handleFolderDialogOpen();
          setMenuAnchorEl(null);
        }}>
          <ListItemIcon>
            <DriveFileMoveIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">{t('move_to_folder')}</Typography>
        </MenuItem>

        <MenuItem 
          onClick={() => {
            handleCopyForm();
            setMenuAnchorEl(null);
          }}
          disabled={copyLoading}
        >
          <ListItemIcon>
            {copyLoading ? (
              <CircularProgress size={20} />
            ) : (
              <ContentCopyIcon fontSize="small" />
            )}
          </ListItemIcon>
          <Typography variant="body2">{t('duplicate')}</Typography>
        </MenuItem>

        <Divider />

        <MenuItem 
          onClick={() => {
            onDelete();
            setMenuAnchorEl(null);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <Typography variant="body2">{t('delete')}</Typography>
        </MenuItem>
      </Menu>

      {/* Klasör Seçim Diyaloğu */}
      <Dialog 
        open={folderDialogOpen} 
        onClose={handleFolderDialogClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          elevation: 24,
          sx: {
            maxHeight: '80vh',
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid #e0e0e0',
          background: 'linear-gradient(to right, #f7f9fc, #edf2f7)',
          py: 2,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}>
          <DriveFileMoveIcon color="primary" />
          <Typography variant="h6">{t('move_form_to_folder')}</Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center', 
              p: 4, 
              height: '200px'
            }}>
              <CircularProgress size={40} />
              <Typography sx={{ mt: 2 }} color="text.secondary">
                {t('loading_folders')}
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ 
                p: 2, 
                mb: 3, 
                borderRadius: 1, 
                bgcolor: 'info.light', 
                color: 'info.contrastText' 
              }}>
                <Typography variant="body2">
                  {t('select_folders_to_move', { formTitle })}
                </Typography>
              </Box>
              
              {selectedFolders.length > 0 && (
                <Box sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 1, 
                  mb: 3, 
                  p: 2,
                  borderRadius: 1,
                  border: '1px dashed',
                  borderColor: 'primary.main',
                  bgcolor: 'rgba(25, 118, 210, 0.05)'
                }}>
                  <Typography variant="subtitle2" sx={{ width: '100%', mb: 1 }}>
                    {t('selected_folders')}:
                  </Typography>
                  {selectedFolders.map(value => {
                    const folder = folderOptions.find(opt => opt.value === value);
                    return folder ? (
                      <Chip 
                        key={value} 
                        label={folder.label}
                        deleteIcon={<CloseIcon fontSize="small" />}
                        onDelete={() => {
                          setSelectedFolders(prev => prev.filter(id => id !== value));
                        }}
                        sx={{
                          fontWeight: 500,
                          bgcolor: 'background.paper',
                          '&:hover': {
                            bgcolor: 'background.default',
                          },
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}
                      />
                    ) : null;
                  })}
                </Box>
              )}
              
              <FolderTreeView 
                folderOptions={folderOptions} 
                selectedFolders={selectedFolders}
                setSelectedFolders={setSelectedFolders}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid #e0e0e0',
          p: 2.5,
          bgcolor: '#f9fafc'
        }}>
          <Button 
            onClick={handleFolderDialogClose} 
            variant="outlined"
            disabled={moveLoading}
            sx={{ px: 2 }}
          >
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleMoveForm} 
            variant="contained" 
            color="primary"
            disabled={selectedFolders.length === 0 || moveLoading}
            sx={{ 
              px: 3,
              position: 'relative',
              '&:disabled': {
                bgcolor: 'action.disabledBackground'
              }
            }}
          >
            {moveLoading ? (
              <>
                <CircularProgress 
                  size={22} 
                  thickness={5}
                  sx={{ 
                    color: 'inherit', 
                    position: 'absolute',
                    left: 12
                  }} 
                />
                <Box sx={{ ml: 3 }}>{t('moving')}...</Box>
              </>
            ) : (
              <>
                <DriveFileMoveIcon sx={{ mr: 1 }} />
                {t('move')}
              </>
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Ağaç görünümü bileşeni (açılır-kapanır klasör yapısı)
const FolderTreeView = ({ folderOptions, selectedFolders, setSelectedFolders }) => {
  const { t } = useTranslation();
  // Açık klasörleri takip etmek için state
  const [expandedFolders, setExpandedFolders] = useState({});
  
  // Klasör yapısını oluştur
  const buildFolderTree = (folderOptions) => {
    // Ana klasörleri ve alt klasörleri grupla
    const parentMap = {};
    
    // Önce ana haritayı oluştur
    folderOptions.forEach(folder => {
      if (!parentMap[folder.parentId]) {
        parentMap[folder.parentId] = [];
      }
      parentMap[folder.parentId].push(folder);
    });
    
    // Sadece üst seviye klasörleri döndür (parentId = 0 olanlar)
    return parentMap[0] || [];
  };
  
  // Bir klasörün alt klasörlerini getir
  const getChildren = (parentId, folderOptions) => {
    return folderOptions.filter(folder => folder.parentId.toString() === parentId.toString());
  };
  
  // Klasörü aç/kapat
  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
  };
  
  // Checkbox değişimi
  const handleFolderSelect = (folderId) => {
    setSelectedFolders(prev => {
      if (prev.includes(folderId)) {
        return prev.filter(id => id !== folderId);
      } else {
        return [...prev, folderId];
      }
    });
  };
  
  // Bir klasörün alt klasörleri var mı kontrol et
  const hasChildren = (folderId, folderOptions) => {
    return folderOptions.some(folder => folder.parentId.toString() === folderId.toString());
  };
  
  // Recursive olarak klasör yapısını render et
  const renderFolderItem = (folder) => {
    const hasSubfolders = hasChildren(folder.value, folderOptions);
    const isExpanded = expandedFolders[folder.value];
    const isSelected = selectedFolders.includes(folder.value);
    const children = getChildren(folder.value, folderOptions);
    
    return (
      <Box key={folder.value} sx={{ mb: 1 }}>
        <Box 
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 1,
            borderRadius: 1,
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'action.hover'
            },
            bgcolor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
            border: isSelected ? '1px solid' : '1px solid transparent',
            borderColor: isSelected ? 'primary.main' : 'transparent',
          }}
        >
          {hasSubfolders ? (
            <IconButton 
              size="small" 
              onClick={() => toggleFolder(folder.value)}
              sx={{ mr: 0.5 }}
            >
              {isExpanded ? (
                <Box sx={{ color: 'text.secondary', fontSize: 18 }}>▼</Box>
              ) : (
                <Box sx={{ color: 'text.secondary', fontSize: 18 }}>▶</Box>
              )}
            </IconButton>
          ) : (
            <Box sx={{ width: 28 }} /> // Boşluk tutucu
          )}
          
          <Checkbox 
            checked={isSelected}
            onChange={() => handleFolderSelect(folder.value)}
            onClick={(e) => e.stopPropagation()}
            size="small"
          />
          
          <FolderIcon 
            sx={{ 
              ml: 0.5, 
              mr: 1.5, 
              color: isSelected ? 'primary.main' : 'action.active' 
            }} 
          />
          
          <Typography 
            variant="body2"
            sx={{ 
              fontWeight: isSelected ? 600 : 400,
              color: isSelected ? 'primary.main' : 'text.primary'
            }}
            onClick={() => handleFolderSelect(folder.value)}
          >
            {folder.label}
          </Typography>
        </Box>
        
        {/* Alt klasörleri render et (açıksa) */}
        {isExpanded && hasSubfolders && (
          <Box sx={{ pl: 4 }}>
            {children.map(childFolder => renderFolderItem(childFolder))}
          </Box>
        )}
      </Box>
    );
  };
  
  // Ana klasörleri bul
  const rootFolders = buildFolderTree(folderOptions);
  
  return (
    <Box 
      sx={{ 
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        maxHeight: '400px',
        overflow: 'auto'
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        {t('folder_structure')}
      </Typography>
      {rootFolders.map(folder => renderFolderItem(folder))}
    </Box>
  );
};

export default FormCard;
